import type { i18n as I18nInstance, ParseKeys, TFunction } from "i18next";

import React, { createContext, useContext } from "react";
import {
  Trans as I18nTrans,
  TransProps,
  useTranslation as useI18nextTranslation,
} from "react-i18next";

import { guestMenuI18n } from "./guest-menu-i18n";
import { GUEST_MENU_NAMESPACE } from "./languages";

/** Defaults to the guest menu instance so no component needs a Provider; the
 * dashboard renders these components with no i18n setup of its own. */
const InstanceContext = createContext<I18nInstance>(guestMenuI18n);

/** Renders a subtree against a specific instance, for showing several languages at once. */
export const GuestMenuI18nProvider = ({
  instance,
  children,
}: {
  instance: I18nInstance;
  children: React.ReactNode;
}) => (
  <InstanceContext.Provider value={instance}>
    {children}
  </InstanceContext.Provider>
);

/** Lets the Storybook config name this type without depending on i18next directly. */
export type GuestMenuT = TFunction;

/** Guest menu translations. */
export const useTranslation = () =>
  useI18nextTranslation(GUEST_MENU_NAMESPACE, {
    i18n: useContext(InstanceContext),
  });

/** `Trans` for copy that wraps part of a sentence in markup. */
export const Trans = <Key extends ParseKeys>(
  props: Omit<TransProps<Key>, "i18n" | "ns">
) => {
  const instance = useContext(InstanceContext);
  return <I18nTrans {...props} i18n={instance} ns={GUEST_MENU_NAMESPACE} />;
};
